import { Copy, Ticket, ChevronRight, ExternalLink } from 'lucide-react';
import { ColumnsType } from 'antd/es/table';
import { message } from 'antd';
import { SingleTicketResponse, TicketFormResponse } from '@/types';
import { TicketColumnEnum } from '@/config';
import {
  formatDateByDayjs,
  getIssueField,
  getTicketCreatorKind,
  getTicketEntityAndPageRoute,
} from '@/utils';
import { entityFieldList } from '@/constants';
import { getStatusTranslationKeyFromRaw } from '@/utils/ticketStatus';

/* ─── Ticket status colors (matching redesign exactly) ─── */
/* Maps ALL possible raw backend status values to the correct redesign color */
const BLUE = { bg: 'hsla(210,65%,55%,0.12)', text: 'hsl(210,60%,42%)' };
const AMBER = { bg: 'hsla(35,80%,55%,0.12)', text: 'hsl(35,70%,40%)' };
const GRAY = { bg: 'hsla(0,0%,60%,0.12)', text: 'hsl(0,0%,40%)' };
const GREEN = { bg: 'hsla(152,60%,48%,0.12)', text: 'hsl(152,60%,38%)' };

export const TICKET_STATUS_HSL: Record<string, { bg: string; text: string }> = {
  // NEW → blue
  open: BLUE, new: BLUE,
  // AWAITING REPLY → amber
  pending: AMBER, waiting: AMBER,
  // ON HOLD → gray
  'on-hold': GRAY, on_hold: GRAY, in_progress: GRAY, hold: GRAY,
  // SOLVED → green
  solved: GREEN, closed: GREEN, resolved: GREEN,
};

/**
 * The statuses a merchant may continue.
 *
 * ⚠️ A SET OF THREE FOR ONE STATE. The UI says "Resolved", the merchant enum
 * says `solved`, and the API can return `closed` — the list's own filter
 * already folds all three together. Matching only the word the design uses
 * would hide the affordance on two of the three, silently: a merchant with a
 * Closed ticket would simply see no way back into the conversation.
 */
export const REOPENABLE_STATUSES = new Set(['solved', 'closed', 'resolved']);

export const getColumns = (
  t: (key: string) => string,
  formData: TicketFormResponse[],
): ColumnsType<any> => [
  {
    title: t('ticket'),
    dataIndex: TicketColumnEnum.ID,
    key: TicketColumnEnum.ID,
    render: (_: unknown, record: SingleTicketResponse) => {
      const rawStatus = record.status?.toLowerCase() ?? 'open';
      const hsl = TICKET_STATUS_HSL[rawStatus] ?? TICKET_STATUS_HSL.open;
      return (
        <div className='flex items-center gap-3'>
          {/* Colored circle icon matching redesign */}
          <div
            className='w-8 h-8 rounded-full flex items-center justify-center shrink-0'
            style={{ background: hsl.bg }}
          >
            <Ticket className='w-4 h-4' style={{ color: hsl.text }} />
          </div>
          <div className='flex flex-col gap-0.5'>
            <span
              className='text-[13px] font-semibold block max-w-[300px] truncate'
              style={{ color: 'rgb(57,57,60)' }}
            >
              {record.subject ?? record.title ?? getIssueField(record)?.name ?? '—'}
            </span>
            <div className='flex items-center gap-1.5'>
              <span className='text-[11px]' style={{ color: 'rgb(115,123,140)' }}>
                {record.id}
              </span>
              {getTicketCreatorKind(record) === 'admin-on-behalf' && (
                <span
                  data-testid='ticket-by-support-badge'
                  className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium'
                  style={{ backgroundColor: 'hsla(210,65%,55%,0.12)', color: 'hsl(210,60%,42%)' }}
                  title={t('ticket_created_by_admin')}
                >
                  {t('ticket_by_support')}
                </span>
              )}
              <Copy
                size={11}
                className='cursor-pointer text-gray-400 hover:text-gray-600 shrink-0'
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(record.id != null ? String(record.id) : '');
                  message.success(String(t('copied')));
                }}
              />
            </div>
          </div>
        </div>
      );
    },
  },
  {
    title: t('type'),
    key: TicketColumnEnum.PLATFORM_CODE,
    responsive: ['md'] as any,
    render: (_: unknown, record: SingleTicketResponse) => {
      // Try issue field first (specific category like "Delivery Delay")
      const issueField = getIssueField(record);
      let categoryName = issueField?.name;

      // Fallback: use the form name (e.g. "Order Issues", "Delivery Issues")
      if (!categoryName && record.ticket_form_id && formData.length > 0) {
        const form = formData.find((f) => f.id === record.ticket_form_id);
        categoryName = form?.name;
      }

      return (
        <span className='text-[12px]' style={{ color: 'rgb(115,123,140)' }}>
          {categoryName ?? '—'}
        </span>
      );
    },
  },
  {
    title: t('status'),
    key: TicketColumnEnum.STATUS,
    render: (_: unknown, record: SingleTicketResponse) => {
      const rawStatus = record.status?.toLowerCase() ?? 'open';
      const hsl = TICKET_STATUS_HSL[rawStatus] ?? {
        bg: 'hsla(0,0%,50%,0.10)',
        text: 'hsl(0,0%,40%)',
      };
      return (
        <div className='flex items-center gap-2'>
          <span
            className='inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap'
            style={{ backgroundColor: hsl.bg, color: hsl.text }}
          >
            {String(t(getStatusTranslationKeyFromRaw(rawStatus)))}
          </span>

          {/*
            R5/R6 — Reopen, on the ticket, where the merchant already is.

            Without it the only route back into a resolved conversation was to
            find the order and raise a "new" ticket, trusting the backend to
            match it — several steps and an act of faith, to add one sentence
            to a thread they are looking at.

            ⭐ IT DOES WHAT THE ROW ALREADY DOES. The row's onClick opens this
            ticket's detail; so does this. The LABEL is the whole feature —
            only one of them tells the merchant that continuing is allowed.
            The status change happens when they COMMENT (R7, backend), so this
            reopens nothing by itself and needs no confirmation. That also
            keeps it distinct from the detail header's Reopen, which patches
            status directly and is permission-gated.

            No stopPropagation: the row handler navigates to the same place,
            so letting the click through is harmless and keeps one code path.
          */}
          {REOPENABLE_STATUSES.has(rawStatus) && (
            <span className='inline-flex items-center px-2 py-1 rounded-full border border-solid border-sd-border text-[11px] font-semibold whitespace-nowrap hover:bg-sd-muted transition-colors'>
              {String(t('reopen'))}
            </span>
          )}
        </div>
      );
    },
  },
  {
    title: t('created'),
    key: 'created_at',
    responsive: ['lg'] as any,
    render: (_: unknown, record: SingleTicketResponse) => (
      <span className='text-[12px] whitespace-nowrap' style={{ color: 'rgb(115,123,140)' }}>
        {formatDateByDayjs(record.created_at, 'DD MMM YYYY, HH:mm')}
      </span>
    ),
  },
  {
    title: t('updated'),
    key: 'updated_at',
    responsive: ['lg'] as any,
    render: (_: unknown, record: SingleTicketResponse) => (
      <span className='text-[12px] whitespace-nowrap' style={{ color: 'rgb(115,123,140)' }}>
        {formatDateByDayjs(record.updated_at, 'DD MMM YYYY, HH:mm')}
      </span>
    ),
  },
  {
    title: '',
    key: 'actions',
    width: 140,
    align: 'end' as const,
    render: (_: unknown, record: SingleTicketResponse) => {
      const external_id =
        record.external_id ??
        record?.fields?.find((field) => entityFieldList.includes(field.id) && !!field.value)
          ?.value ?? undefined;

      if (external_id) {
        const entityData = getTicketEntityAndPageRoute(
          external_id ?? '',
          record.entity_uid ?? undefined,
        );

        if (entityData?.singlePageUrl) {
          return (
            <button
              className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer bg-transparent border-0 text-gray-500 hover:text-gray-700 whitespace-nowrap'
              onClick={(e) => {
                e.stopPropagation();
                window.open(entityData.singlePageUrl!, '_blank');
              }}
              title={external_id}
            >
              <span className='text-[11px] font-medium' style={{ fontFamily: 'monospace' }}>
                {external_id}
              </span>
              <ExternalLink size={12} className='shrink-0' />
            </button>
          );
        }
      }

      return (
        <div className='flex justify-end w-full'>
          <ChevronRight size={16} style={{ color: 'rgb(156,163,175)' }} />
        </div>
      );
    },
  },
];
